<script setup lang="ts">
import type { Cluster } from '@/services/types/api.types';

type Idea = Cluster['ideas'][number];

interface IdeaOption {
  id: string;
  label: string;
  description?: string;
  value: { cluster: Cluster; idea: Idea };
}

const props = defineProps<{
  modelValue: boolean;
  ideaContext: { sourceTheme: Cluster; idea: Idea } | null;
  options: IdeaOption[];
  selectedId: string | null;
  search: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:selectedId', value: string | null): void;
  (e: 'selectOption', value: { cluster: Cluster; idea: Idea } | null): void;
  (e: 'update:search', value: string): void;
  (e: 'confirm'): void;
}>();

function close() {
  emit('update:modelValue', false);
}

function confirm() {
  emit('confirm');
}

function selectOption(option: IdeaOption) {
  emit('update:selectedId', option.id);
  emit('selectOption', option.value);
}
</script>

<template>
  <q-dialog :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)" @hide="close">
    <q-card style="min-width: 440px">
      <q-card-section class="text-h6">
        {{ $t('consultation.ai.mergeIdeas.dialogTitle') }}
      </q-card-section>
      <q-card-section>
        <i18n-t
          v-if="ideaContext"
          keypath="consultation.ai.mergeIdeas.dialogMessage"
          tag="div"
          class="text-body2 q-mb-sm"
        >
          <template #idea>
            <span class="text-weight-bold">{{ ideaContext.idea.representative_idea }}</span>
          </template>
        </i18n-t>
        <q-input
          :model-value="search"
          :label="$t('consultation.ai.mergeIdeas.searchLabel')"
          dense
          outlined
          class="q-mb-sm"
          debounce="150"
          @update:model-value="emit('update:search', $event)"
        />
        <div class="text-caption text-grey-7 q-mb-xs">{{ $t('consultation.ai.mergeIdeas.selectLabel') }}</div>
        <q-virtual-scroll
          style="max-height: 320px"
          class="rounded-borders"
          :items="options"
          :virtual-scroll-item-size="72"
        >
          <template #default="{ item }">
            <q-item
              :key="item.id"
              clickable
              :active="selectedId === item.id"
              active-class="bg-blue-1"
              @click="selectOption(item)"
            >
              <q-item-section>
                <q-item-label class="ellipsis">{{ item.label }}</q-item-label>
                <q-item-label caption class="ellipsis">
                  {{ item.description }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
          <template #after>
            <div v-if="options.length === 0" class="q-pa-md text-grey-6">
              {{ $t('consultation.ai.mergeIdeas.noResults') }}
            </div>
          </template>
        </q-virtual-scroll>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat color="primary" :label="$t('global.cancel')" @click="close" />
        <q-btn
          color="primary"
          :label="$t('consultation.ai.mergeIdeas.confirm')"
          :disable="!selectedId"
          @click="confirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
